import useAddRequestStore from 'store/addRequestStore';
import AddRequestHeader from './AddRequestHeader';
import Dropdown from './DropDown';
import RangeFilter from './RangeFilter';
import { useState } from 'react';

interface BudgetTimeline {
    isEditMode?: boolean;
}

export default function BudgetTimeline({ isEditMode }: BudgetTimeline) {
    const { requestData, setRequestData, clearErrors, errors, setErrors } = useAddRequestStore();

    return (
        <>
            <AddRequestHeader title="Set Your Budget & Timeline" description="Indicate your budget range and desired move-in date" />
            <div className="lg:space-y-10 space-y-5 mt-7 lg:mt-8">
                <ItemContainer
                    title="Budget"
                    description="Enter or adjust the minumum & maximum budget you'd like to allocate"
                    children={
                        <>
                            <div className="flex flex-col space-y-4">
                                <RangeFilter
                                    showBorderAndText={false}
                                    unit="€"
                                    classNames={`${isEditMode ? 'w-full' : 'default-lg:w-2/3 default-xl:w-5/12 default-md:w-2/3 default-sm:w-full'} `}
                                    value={[requestData.minBudget, requestData.maxBudget]}
                                    onChanged={(value) => {
                                        if (value[0] > value[1]) {
                                            setErrors({ ...errors, maxBudget: 'Maximum budget cannot be less than minimum budget' });
                                        } else {
                                            clearErrors('minBudget');
                                            clearErrors('maxBudget');
                                        }
                                        setRequestData({ minBudget: value[0], maxBudget: value[1] });
                                    }}
                                    limit={[0, 50000000]}
                                    steps={[
                                        { max: 500000, step: 25000 },
                                        { max: 1000000, step: 50000 },
                                        { max: 5000000, step: 100000 },
                                        { max: 10000000, step: 500000 },
                                        { max: 50000000, step: 1000000 },
                                    ]}
                                />
                            </div>
                            {(errors.minBudget || errors.maxBudget) && <p className="text-red-500 text-sm !mt-2">{errors.minBudget || errors.maxBudget}</p>}
                        </>
                    }
                />
                <ItemContainer
                    title="Urgency"
                    description="Select your preferred timeline for finding a property. This helps us prioritize properties that match your urgency level."
                    children={
                        <>
                            <div className={` ${isEditMode ? 'w-full' : 'default-lg:w-2/3 default-xl:w-10/12 default-md:w-2/3 default-sm:w-full'} `}>
                                <Dropdown
                                    options={[
                                        'Immediate (0-1 month)',
                                        'Short Term (1-3 months)',
                                        'Medium Term (3-6 months)',
                                        'Long Term (6+ months)',
                                        'Flexible',
                                    ]}
                                    isShown={false}
                                    heightClass="h-12 lg:h-[52px]"
                                    isEditMode={isEditMode}
                                    value={requestData.urgency}
                                    onChange={(value) => {
                                        setRequestData({ urgency: value as string });
                                        clearErrors('urgency');
                                    }}
                                    showClearButton={false}
                                />
                            </div>
                            {errors.urgency && <p className="text-red-500 text-sm !mt-2">{errors.urgency}</p>}
                        </>
                    }
                />
                <ItemContainer
                    title="Agent Commission"
                    description="Specify the percentage of commission you are willing to offer the agent that brings the listing for the buyer. This helps ensure clear expectations and smooth negotiations."
                    children={
                        <>
                            <div className={` ${isEditMode ? 'w-full' : 'default-lg:w-2/3 default-xl:w-10/12 default-md:w-2/3 default-sm:w-full'} `}>
                                <Dropdown
                                    options={['0%', '1%', '2%', '3%', '4%', '5%', '6%', 'Custom Percentage']}
                                    isShown={false}
                                    heightClass="h-12 lg:h-[52px]"
                                    isEditMode={isEditMode}
                                    value={requestData.commission}
                                    onChange={(value) => {
                                        if (value === 'Custom Percentage') {
                                            setRequestData({ commission: value });
                                        } else {
                                            clearErrors('commission');
                                            setRequestData({ commission: value });
                                        }
                                    }}
                                    showClearButton={false}
                                />
                            </div>
                            {errors.commission && <p className="text-red-500 text-sm !mt-2">{errors.commission}</p>}
                        </>
                    }
                    showBorder={false}
                />
                {(requestData?.commission as string)?.startsWith('Custom') && (
                    <div
                        className={`relative flex items-center ${isEditMode ? 'w-full' : 'default-lg:w-2/3 default-xl:w-5/12 default-md:w-2/3 default-sm:w-full'} `}
                    >
                        <input
                            type="number"
                            className="w-full pl-4 py-3.5  border rounded-l-md focus:outline-none"
                            value={requestData.customCommission}
                            onChange={(e) => {
                                setRequestData({ customCommission: Number(e.target.value) });
                                clearErrors('commission');
                            }}
                        />
                        <div className="px-4 py-3.5 bg-gray-50 border-y border-r rounded-r-md">%</div>
                    </div>
                )}
            </div>
        </>
    );
}

interface Props {
    title: string;
    description: string;
    children?: React.ReactNode;
    showBorder?: boolean;
}

function ItemContainer({ title, description, children, showBorder = true }: Props) {
    return (
        <>
            <div className="space-y-2 lg:space-y-3">
                <label htmlFor="title" className="block text-base  font-semibold text-light-black">
                    {title}
                </label>
                <p className="text-[12px]  text-light-black/80 mb-2">{description}</p>
                {children}
            </div>
            {showBorder && <div className="border border-gray-100  w-full " />}
        </>
    );
}
